import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import {
  ColumnGroup,
  ColumnOptions,
  GridRowType,
  LoaderService,
  MessageService,
  SelectableType,
  ValidationResultService,
} from '@shared';
import {
  ChangesetService,
  GlobalEventsService,
  IChangeset,
} from '@shared-data';
import {
  GridRowAlacarteAvailabilityChangeset,
  GridRowModifierCodesChangeset,
  GridRowOverride,
  GridRowPricingChangeset,
} from '../../models';
import {
  groupBy,
  GroupDescriptor,
  GroupResult,
} from '@progress/kendo-data-query';
import {
  PENDING_STRATEGIES,
  PendingType,
} from '../../models/column-strategy.tokens';
import { GridDataResult } from '@progress/kendo-angular-grid';

type ValidatedChangesetRow = IChangeset & GridRowType & SelectableType;

@Component({
  selector: 'app-pending-changes-grid',
  templateUrl: './pending-changes-grid.component.html',
  styleUrl: './pending-changes-grid.component.scss',
  providers: [
    {
      provide: PENDING_STRATEGIES,
      useValue: {
        pricing: new GridRowPricingChangeset(),
        availability: new GridRowAlacarteAvailabilityChangeset(),
        overrides: new GridRowOverride(),
        modifiercodes: new GridRowModifierCodesChangeset()
      },
    },
  ],
})
export class PendingChangesGridComponent implements OnChanges {
  @Input()
  type: PendingType = 'pricing';

  @Input()
  labOnly: boolean = false;

  columns: ColumnOptions[] = [];

  columnGroups: ColumnGroup[] = [];

  group: GroupDescriptor[] = [{ field: 'tierName' }];

  gridData!: ValidatedChangesetRow[] | GroupResult[];

  changeset: GridDataResult = { data: [], total: 0};

  height: number = 500;

  tierToRemoveChanges: number | null = null;

  @Output()
  dataChanged: EventEmitter<GridDataResult> = new EventEmitter<
    GridDataResult
  >();

  constructor(
    @Inject(PENDING_STRATEGIES)
    private strategies: Record<PendingType, GridRowType>,
    private changesetService: ChangesetService,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private globalEventsService: GlobalEventsService,
    private validationResultService: ValidationResultService,
  ) {}

  ngOnChanges() {
    if (this.type) {
      const strat = this.strategies?.[this.type as PendingType];
      if (!strat) return;
      this.columns = strat
        .getColumns()
        .filter((c: ColumnOptions) => c.visibleInQuickView);

      this.columnGroups = strat.getColumnGroups(this.columns);
      this.loadPendingChanges();
    }
  }

  loadPendingChanges() {
    this.loaderService.show();
    this.changesetService.getPendingChanges(this.type, this.labOnly).subscribe({
      next: (changes) => {
        this.populateGrid(changes);
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load pending changes');
        this.loaderService.hide();
      },
    });
  }

  removeRow(changeset: ValidatedChangesetRow) {
    this.loaderService.show();
    this.changesetService
      .cancelPendingChange(changeset.id, this.type)
      .subscribe({
        next: () => {
          this.messageService.success('Pending change successfully removed');
          this.loadPendingChanges();
          this.globalEventsService.updatePendingChanges();
          this.loaderService.hide();
        },
        error: (error) => {
          this.messageService.error('Failed to remove pending change');

          var validationError = error?.error;

          if (validationError) {
            this.validationResultService.show(validationError);
          }

          this.loaderService.hide();
        },
      });
  }

  populateGrid(changes: GridDataResult) {
    this.changeset = changes;
    this.changeset.data.forEach((change) => {
      change.type = this.type;
      change.locked = true;
      change.errors = new Map<string, string>();
      change.warnings = new Map<string, string>();
      change.dirty = new Map<string, boolean>();
      change.originalValues = new Map<string, any>();
      change.selected = true;
    });

    this.height = Math.min(500, 200 + 50 * this.changeset.data.length);

    this.gridData = groupBy(this.changeset.data, this.group);
    this.dataChanged.emit(this.changeset);
  }

  toggleSelectedTiers(tierName: string, isChecked: string | boolean) {
    if (isChecked) {
      this.changeset.data.forEach((change) => {
        if (change.tierName === tierName) {
          change.selected = true;
        }
      });
    } else {
      this.changeset.data.forEach((change) => {
        if (change.tierName === tierName) {
          change.selected = false;
        }
      });
    }
  }

  onConfirmRemoveChanges() {
    if (this.tierToRemoveChanges == null) return;
    this.changesetService
      .removePendingChanges(this.type, this.tierToRemoveChanges!)
      .subscribe({
        next: () => {
          this.messageService.success(
            'All pending changes successfully removed',
          );
          this.loadPendingChanges();
          this.globalEventsService.updatePendingChanges();
          this.tierToRemoveChanges = null;
        },
        error: (error) => {
          this.messageService.error('Failed to remove pending changes');
          var validationError = error?.error;

          if (validationError) {
            this.validationResultService.show(validationError);
          }

          this.tierToRemoveChanges = null;

          this.loaderService.hide();
        },
      });
  }
}
